import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, TextField, makeStyles } from '@material-ui/core';
import Theme from '../ui/Theme';
import service from '../../apis/service';
import { showProgress } from '../../redux/actions/DealershipActions';
import { ErrorToast } from '../utils/Utils';
import EmployeeList from './EmployeeList';
import RuleConditionsPopup from './RuleConditionsPopup';
import DealershipList from './DealershipList';
import RuleList from './RuleList';
import { TabNativeContent } from '../common/Tab';
import { apiEndpoints } from '../../constants/apiEndpoints';

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: Theme.palette.secondary.main,
  },
  scrollContent: {
    maxHeight: '60vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': { width: '10px' },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 5px grey',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'lightGrey',
      borderRadius: '100px',
    },
    marginTop: '20px',
    marginLeft: '10px',
  },
  searchText: {
    width: '915px',
    height: '42px',
    background: '#FAFAFA',
    border: '1px solid #E2E2E2',
    padding: '8px',
    fontFamily: 'Montserrat',
    fontWeight: '500',
    fontSize: '14px',
    borderRadius: '8px',
  },
}));

const DEALERSHIP_TAB = 'dealerships';
const RULES_TAB = 'rules';

const TABS = {
  0: DEALERSHIP_TAB,
  1: RULES_TAB,
};

export default function CustomGroupMainContent() {
  const classes = useStyles();

  const dispatch = useDispatch();
  const customGroupDetails = useSelector((state) => state.customGroupsData.selectedCustomGroupDetails);

  const [tabId, setTabId] = useState(0);
  const [showEmployeePopup, setShowEmployeePopup] = useState(false);
  const [showRulePopup, setShowRulePopup] = useState(false);
  const [dealershipEmployees, setDealershipEmployees] = useState([]);
  const [selectedRuleId, setSelectedRuleId] = useState(null);

  const [filteredArray, setFilteredArray] = useState([]);

  const [searchVal, setSearchVal] = useState('');

  useEffect(() => {
    setTabId(0);
    setShowEmployeePopup(false);
    setDealershipEmployees([]);
    setSearchVal('');
    setShowRulePopup(false);
    setFilteredArray(customGroupDetails.dealerships);
    setSelectedRuleId(null);
  }, [customGroupDetails]);

  useEffect(() => {
    setSearchVal('');
    if (TABS[tabId] === DEALERSHIP_TAB) setFilteredArray(customGroupDetails.dealerships);
    else setFilteredArray(customGroupDetails.rules);
  }, [tabId]);

  const getDealershipEmployeesForCustomGroup = async (dealershipId) => {
    dispatch(showProgress(true));
    await service
      .get(apiEndpoints.CG_EMPLOYEES.replace('CG_ID', customGroupDetails.id).replace('DEALER_ID', dealershipId))
      .then((response) => {
        setDealershipEmployees(response || []);
        setShowEmployeePopup(true);
        dispatch(showProgress(false));
      })
      .catch((error) => {
        ErrorToast(error);
        dispatch(showProgress(false));
      });
  };

  const handleRuleClick = (id) => {
    setShowRulePopup(true);
    setSelectedRuleId(id);
  };

  const handleSearch = (e) => {
    const input = e.target.value;
    setSearchVal(input);
    let filterArrayTemp = [];
    if (TABS[tabId] === DEALERSHIP_TAB) {
      filterArrayTemp = input
        ? customGroupDetails.dealerships.filter((item) => item.name.toLowerCase().includes(input.toLowerCase()))
        : customGroupDetails.dealerships;
    } else {
      filterArrayTemp = input
        ? customGroupDetails.rules.filter((item) => item.name.toLowerCase().includes(input.toLowerCase()))
        : customGroupDetails.rules;
    }
    setFilteredArray(filterArrayTemp);
  };

  const showSearch = useMemo(() => {
    if (TABS[tabId] === DEALERSHIP_TAB) {
      return customGroupDetails?.dealerships?.length;
    }
    return customGroupDetails?.rules?.length;
  }, [tabId, customGroupDetails]);

  const tabs = [
    { label: `Dealerships (${customGroupDetails.dealerships?.length || 0})`, index: 0 },
    { label: `Rules (${customGroupDetails.rules?.length || 0})`, index: 1 },
  ];

  return (
    <div className={classes.root}>
      <TabNativeContent setTabValue={setTabId} tabValue={tabId} tabs={tabs} />
      {showSearch ? (
        <Grid container direction="row" style={{ margin: '15px 0' }}>
          <Grid item>
            <TextField
              id="searchId"
              name="search"
              required
              autoComplete="off"
              autoSave="off"
              onChange={handleSearch}
              placeholder={TABS[tabId] === DEALERSHIP_TAB ? 'Search Dealerships' : 'Search Rules'}
              value={searchVal}
              InputProps={{
                disableUnderline: true,
                className: classes.searchText,
              }}
              rows={1}
              style={{ marginLeft: '15px' }}
            />
          </Grid>
        </Grid>
      ) : null}
      <div className={classes.scrollContent}>
        {TABS[tabId] === DEALERSHIP_TAB ? (
          <DealershipList
            dealerships={customGroupDetails?.dealerships || []}
            filteredArray={filteredArray || []}
            getDealershipEmployeesForCustomGroup={getDealershipEmployeesForCustomGroup}
          />
        ) : (
          <RuleList
            filteredArray={filteredArray || []}
            rules={customGroupDetails?.rules || []}
            handleRuleClick={handleRuleClick}
          />
        )}
      </div>
      {showEmployeePopup && (
        <EmployeeList
          close={() => {
            setShowEmployeePopup(false);
            setDealershipEmployees([]);
          }}
          employeesList={dealershipEmployees}
        />
      )}
      {showRulePopup && (
        <RuleConditionsPopup
          close={() => {
            setShowRulePopup(false);
            setSelectedRuleId(null);
          }}
          rule={customGroupDetails.rules.find((i) => i.id === selectedRuleId)}
        />
      )}
    </div>
  );
}
